import { Link } from 'react-router';
import type { ProgramHypothesisRef } from '../api/useHypoteseProgrammer.ts';
import { useHypoteseConfig } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { hvem, statusNavn, stor } from '../hypoteser/lesning.ts';
import { kortDatoAv } from '../hypoteser/setning.ts';
import { Ikon } from '../status/Ikon.tsx';
import { hypoteseOppforing } from '../status/status.ts';

/**
 * Lenken fra programmet til hypotesen det ble laget fra (H3.4, konseptets skjerm 8, 9, 17a og 17b).
 * Trimmet etter rom av tjeneren (P8): uten Hypoteser kommer bare nummeret og datoen — da står det
 * «Laget fra hypotese H-142», uten tittel, utfall og lenke. Kh7 speilvendt.
 */

const tilHypotesen = (nr: number) => `/h/registeret/${nr}`;

/** Smulen på programsiden: «fra H-142 Fortsett å se øverst på mobil» med teleskopet, som lenke (skjerm 8). */
export function SmuleHypotese({ hyp }: { hyp: ProgramHypothesisRef }) {
  const nr = `H-${hyp.number}`;
  return (
    <span className="panel-prg-smule-hyp" data-testid="prg-smule-hypotese">
      <Ikon navn="telescope-outline" />
      {hyp.title ? (
        <Link to={tilHypotesen(hyp.number)}>
          fra {nr} {hyp.title}
        </Link>
      ) : (
        <span>fra {nr}</span>
      )}
    </span>
  );
}

/**
 * Raden nederst på et avsluttet program (skjerm 9 og 17a): hva rapporten er for hypotesen, hvem som
 * skriver og hvem som bekrefter — eller, etter konklusjonen, utfallet og om konklusjonen bygde på
 * denne rapporten. Uten Hypoteser (17b): nummeret og datoen.
 */
export function HypoteseRad({ hyp }: { hyp: ProgramHypothesisRef }) {
  const megId = useMe().data?.id;
  const medRom = hyp.title !== undefined;
  const config = useHypoteseConfig(medRom);
  const nr = `H-${hyp.number}`;

  if (!medRom) {
    return (
      <div className="panel-prg-hypoteserad" data-testid="prg-hypoteserad">
        <Ikon navn="telescope-outline" className="panel-prg-hypoteserad-ikon" />
        <span className="panel-prg-hypoteserad-tittel">{`Laget fra hypotese ${nr}${hyp.concludedAt ? ` · konkludert ${kortDatoAv(hyp.concludedAt)}` : ''}`}</span>
      </div>
    );
  }

  const ferdig = hyp.concludedAt !== undefined && (hyp.status === 'konkludert' || hyp.status === 'stoppet');
  const noekkel = hyp.status === 'konkludert' && hyp.outcome ? hyp.outcome : (hyp.status ?? 'testes');
  const navn = statusNavn(config.data, { status: hyp.status ?? 'testes', outcome: hyp.outcome });
  // Leietakerens ord for utfallet; til det er hentet, står raden ikke — enumverdien vises aldri (DD-56).
  if (ferdig && !navn) return null;
  return (
    <div className="panel-prg-hypoteserad" data-testid="prg-hypoteserad">
      <Ikon navn="telescope-outline" className="panel-prg-hypoteserad-ikon" />
      <span className="panel-prg-hypoteserad-tittel">{ferdig ? `${nr} ${hyp.title}` : `Et forslag til konklusjonen på ${nr}`}</span>
      <Link to={tilHypotesen(hyp.number)} className="panel-knapp-lenke panel-prg-hypoteserad-lenke">
        Åpne {nr}
      </Link>
      <span className="panel-prg-hypoteserad-meta">
        {ferdig ? (
          <>
            <Ikon navn={hypoteseOppforing(noekkel).ikon} />
            <span>{`${navn} ${kortDatoAv(hyp.concludedAt!)}${hyp.conclusionUsesThisReport ? ' · konklusjonen bygde på denne rapporten' : ''}`}</span>
          </>
        ) : (
          <span>{`${stor(hvem(hyp.owner, megId, 'du'))} skriver konklusjonen og kan bruke rapporten som utgangspunkt · ${hvem(hyp.decider, megId, 'du')} bekrefter`}</span>
        )}
      </span>
    </div>
  );
}

